/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import { Box, FormHelperText, FormLabel, MenuItem, Stack, TextField } from '@mui/material';
import { useTimeFormat, parseClock, from24Hour, to24Hour } from '../../utils/timeFormat';

const pad = (n) => String(n).padStart(2, '0');
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));
const HOURS_12 = Array.from({ length: 12 }, (_, i) => pad(i + 1));
const HOURS_24 = Array.from({ length: 24 }, (_, i) => pad(i));

/**
 * A time of day, stored as "HH:mm" (24-hour) whatever the user sees.
 *
 * Replaces <input type="time">, whose 12/24-hour display follows the
 * operating system and cannot be set by the page — so it would ignore the
 * user's choice on their profile. Shows Hour + Minute in 24-hour mode, and
 * Hour + Minute + AM/PM in 12-hour mode.
 */
const TimeInput = ({
  label, value, onChange, required = false, disabled = false,
  helperText, error = false, size = 'medium', fullWidth = false
}) => {
  const format = useTimeFormat();
  const parsed = parseClock(value);
  const minutes = parsed ? pad(parsed.minutes) : '';
  const parts12 = parsed ? from24Hour(parsed.hours) : { hour: '', period: '' };

  const emit = (hours, mins) => onChange(`${pad(hours)}:${mins || '00'}`);

  const onHour = (raw) => {
    if (raw === '') return onChange('');
    if (format === '24h') return emit(Number(raw), minutes);
    return emit(to24Hour(raw, parts12.period || 'AM'), minutes);
  };
  const onMinute = (raw) => {
    if (!parsed) return emit(format === '24h' ? 0 : to24Hour('12', 'AM'), raw);
    return emit(parsed.hours, raw);
  };
  const onPeriod = (raw) => emit(to24Hour(parts12.hour || '12', raw), minutes);

  const fieldSx = { minWidth: 76, flex: 1, bgcolor: 'background.paper' };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <FormLabel required={required} error={error} sx={{ display: 'block', fontSize: '0.85rem', mb: 0.75 }}>
          {label}
        </FormLabel>
      )}
      <Stack direction="row" spacing={1}>
        <TextField
          select size={size} label="Hour" sx={fieldSx} disabled={disabled} error={error}
          value={format === '24h' ? (parsed ? pad(parsed.hours) : '') : parts12.hour}
          onChange={(e) => onHour(e.target.value)}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {(format === '24h' ? HOURS_24 : HOURS_12).map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
        </TextField>
        <TextField
          select size={size} label="Min" sx={fieldSx} disabled={disabled} error={error}
          value={minutes}
          onChange={(e) => onMinute(e.target.value)}
        >
          {MINUTES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
        {format === '12h' && (
          <TextField
            select size={size} label="AM/PM" sx={fieldSx} disabled={disabled} error={error}
            value={parts12.period}
            onChange={(e) => onPeriod(e.target.value)}
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </TextField>
        )}
      </Stack>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Box>
  );
};

export default TimeInput;
